// ✅ src/pages/VehicleV2/PeriodicEntry.jsx  (UPDATED FULL FILE)
// Flow:
// 1) Page loads -> init API loads branches / stores / vehicle types / subtypes
// 2) Default branch/store applied from init
// 3) Search -> calls list API with filters
// 4) View All -> clears search fields and reloads listing
// 5) Details -> navigate to MaintenanceListing
//
// Backend expected:
//   app.use("/api/periodicEntry", periodicEntry);
//
// APIs used:
//   GET  https://nissi-india.com/api/periodicEntry/init?loginUserId=&branchId=&branchType=
//   GET  https://nissi-india.com/api/periodicEntry/stores?branchId=
//   GET  https://nissi-india.com/api/periodicEntry/list?loginUserId=&branchId=&branchType=&branchSelect=&storeId=&vehicleTypeIndex=&vehicleSubtypeId=&vehicleCode=&vehicleName=&vehicleNo=&page=&limit=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + listLoading states
// - safe init / store / list handling
// - cleaner search / reset / pagination handling
// - consistent card / header / button style

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaFilter, FaEye, FaRedo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/periodicEntry";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? Math.trunc(x) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const PeriodicEntry = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("userid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);

  const [filters, setFilters] = useState({
    branchSelect: "",
    storeId: "",
    vehicleTypeIndex: "",
    vehicleSubtypeId: "",
    vehicleCode: "",
    vehicleName: "",
    vehicleNo: "",
  });

  const [list, setList] = useState([]);

  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const isBusy = pageLoading || listLoading;

  const totalPages = useMemo(() => {
    const t = Math.ceil((total || 0) / limit);
    return t <= 0 ? 1 : t;
  }, [total, limit]);

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const renderText = useCallback((row, keys, fallback = "-") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v);
      }
    }
    return fallback;
  }, []);

  const resetSearchFilters = useCallback(
    (prev) => ({
      ...prev,
      vehicleTypeIndex: "",
      vehicleSubtypeId: "",
      vehicleCode: "",
      vehicleName: "",
      vehicleNo: "",
    }),
    []
  );

  /* =========================================================
     INIT
     ========================================================= */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId,
          branchId,
          branchType,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      const nextBranches = data?.branches || [];
      const nextStores = data?.stores || [];
      const nextVehicleTypes = data?.vehicleTypes || [];
      const nextVehicleSubtypes = data?.vehicleSubtypes || [];

      setBranches(nextBranches);
      setStores(nextStores);
      setVehicleTypes(nextVehicleTypes);
      setVehicleSubtypes(nextVehicleSubtypes);

      const nextBranchSelect = toStr(data?.branchId) || "";
      const nextStoreId = toStr(data?.defaultStoreId) || "";

      setFilters((prev) => ({
        ...prev,
        branchSelect: nextBranchSelect || prev.branchSelect,
        storeId: nextStoreId || prev.storeId,
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, branchType, commonHeaders]);

  /* =========================================================
     LOAD STORES BY BRANCH
     ========================================================= */
  const loadStoresByBranch = useCallback(
    async (selectedBranchId) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/stores`, {
          params: {
            branchId: selectedBranchId,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "Store load failed");
          return;
        }

        const nextStores = data?.stores || [];
        setStores(nextStores);

        setFilters((prev) => ({
          ...prev,
          storeId: nextStores?.[0]?.id ? String(nextStores[0].id) : "",
        }));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Store load failed"
        );
      } finally {
        setListLoading(false);
      }
    },
    [commonHeaders]
  );

  /* =========================================================
     LIST
     ========================================================= */
  const loadList = useCallback(
    async (pageNo = 1, overrideFilters = null) => {
      const f = overrideFilters || filters;

      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId,
            branchId,
            branchType,

            branchSelect: clean(f.branchSelect),
            storeId: clean(f.storeId),

            vehicleTypeIndex: clean(f.vehicleTypeIndex),
            vehicleSubtypeId: clean(f.vehicleSubtypeId),

            vehicleCode: clean(f.vehicleCode),
            vehicleName: clean(f.vehicleName),
            vehicleNo: clean(f.vehicleNo),

            page: pageNo,
            limit,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "List fetch failed");
          return;
        }

        setList(data?.list || []);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, pageNo));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "List fetch failed"
        );
      } finally {
        setListLoading(false);
      }
    },
    [filters, loginUserId, branchId, branchType, limit, commonHeaders]
  );

  /* ---------------- handlers ---------------- */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (name === "branchSelect") {
        setFilters((prev) => ({
          ...prev,
          branchSelect: value,
          storeId: "",
        }));

        if (value && value !== "Select") {
          loadStoresByBranch(value);
        } else {
          setStores([]);
        }
        return;
      }

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [loadStoresByBranch]
  );

  const handleSearch = useCallback(async () => {
    await loadList(1);
    toast.success("Search completed");
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const reset = resetSearchFilters(filters);
    setFilters(reset);
    await loadList(1, reset);
    toast.success("Showing all records");
  }, [filters, loadList, resetSearchFilters]);

  const handlePage = useCallback(
    async (p) => {
      if (p < 1 || p > totalPages || listLoading) return;
      await loadList(p);
    },
    [totalPages, listLoading, loadList]
  );

  const handleDetails = useCallback(
    (row) => {
      navigate(`/vehicle/MaintenanceListing?vehicleId=${row.fld_vehicleid}`);
    },
    [navigate]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (!filters.branchSelect) return;
    if (!filters.storeId) return;
    loadList(1);
  }, [filters.branchSelect, filters.storeId, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PERIODIC ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Periodic Entry
              </h1>

              <span className={pillClass}>{total} Records</span>
            </div>

            <div className="flex items-center gap-3">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading ? "Loading..." : "Fetching list..."}
                </span>
              )}

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${showFilters ? (isDark ? "bg-gray-800" : "bg-gray-50") : ""} ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`${sectionClass} mb-6`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 uppercase">
                <FaFilter />
                Filtration Area
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchSelect"
                    value={filters.branchSelect}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Store */}
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {stores.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Main Type */}
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeIndex"
                    value={filters.vehicleTypeIndex}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {vehicleTypes.map((t, idx) => (
                      <option
                        key={t.vehicleTypeIndex ?? t.id ?? idx}
                        value={String(t.vehicleTypeIndex ?? t.id ?? "")}
                      >
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Code */}
                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                {/* Vehicle Subtype */}
                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    name="vehicleSubtypeId"
                    value={filters.vehicleSubtypeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {vehicleSubtypes.map((st) => (
                      <option key={st.id} value={String(st.id)}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Number */}
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNo"
                    value={filters.vehicleNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                    disabled={isBusy}
                  />
                </div>

                {/* Vehicle Name */}
                <div className="md:col-span-3">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm opacity-80">
                  {isBusy ? "Processing..." : "Ready"}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>

                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg border font-semibold ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <FaRedo />
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {listLoading || pageLoading ? (
              <div className="text-center py-12">Loading...</div>
            ) : list.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
                <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Company Name
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Details
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {list.map((row, idx) => (
                      <tr
                        key={row.id ?? row.fld_vehicleid ?? idx}
                        className={`${
                          isDark ? "bg-gray-900 hover:bg-gray-800" : "bg-white hover:bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["VehicleType", "vehicleMainType", "mainType"])}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["fld_vehicle_code", "vehicleCode", "code"])}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["fld_vehicle_regno", "vehicleRegNo", "number"])}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["fld_vehicle_name", "vehicleName", "name"])}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["vehicleMainType", "fld_vehicletypename", "type"])}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {renderText(row, ["fld_companyname", "companyName", "company"], "-")}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleDetails(row)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                          >
                            <FaEye />
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6">
            <nav className="flex items-center space-x-2">
              <button
                className={`px-3 py-1 border rounded ${
                  isDark
                    ? "bg-gray-800 text-white border-gray-700"
                    : "bg-white text-gray-700 border-gray-300"
                } ${page <= 1 || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={() => handlePage(page - 1)}
                disabled={page <= 1 || isBusy}
              >
                Prev
              </button>

              <span
                className={`px-3 py-1 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Page {page} / {totalPages}
              </span>

              <button
                className={`px-3 py-1 border rounded ${
                  isDark
                    ? "bg-gray-800 text-white border-gray-700"
                    : "bg-white text-gray-700 border-gray-300"
                } ${page >= totalPages || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={() => handlePage(page + 1)}
                disabled={page >= totalPages || isBusy}
              >
                Next
              </button>
            </nav>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default PeriodicEntry;